/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable camelcase */
/* eslint-disable import/prefer-default-export */
import _ from 'lodash';
import { SUPPORTED_NETWORKS } from '../../../../constants/socialMedia';

// Helper function to get the latest metric from a post
const getLatestMetric = metrics => {
  if (!Array.isArray(metrics) || metrics.length === 0) {
    return null;
  }

  // Sort by date and get the latest one using destructuring
  const [latestMetric] = metrics.sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return latestMetric;
};

const calculateEngagementRate = (likes, comments, shares, views) => {
  if (views === 0) return 0;
  return (likes + comments + shares) / views;
};

// Helper function to extract metric values with fallbacks
const extractMetricValues = metric => {
  const {
    likes = 0,
    comments = 0,
    shares = 0,
    views = 0,
    dislikes = 0,
    plays = 0,
    reach = 0,
    video_views_organic = 0,
    video_views_paid = 0,
  } = metric || {};

  return {
    likes,
    comments,
    shares,
    dislikes,
    views: views || plays,
    reach,
    plays,
    video_views_organic,
    video_views_paid,
  };
};

// Helper function to process daily breakdown from posts
const processDailyBreakdown = posts => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return {};
  }

  const dailyBreakdown = {};

  // Get the date range for the last 3 months
  const today = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(today.getMonth() - 3);

  // Initialize all dates in the last 3 months with 0s
  const currentDate = new Date(threeMonthsAgo);
  while (currentDate <= today) {
    const dateString = currentDate.toISOString().split('T')[0];
    dailyBreakdown[dateString] = {
      likes: 0,
      comments: 0,
      shares: 0,
      dislikes: 0,
      views: 0,
      plays: 0,
      reach: 0,
      video_views_organic: 0,
      video_views_paid: 0,
      engagementRate: 0,
      videoCompletionRate: 0,
    };
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Process each post's metrics
  posts.forEach(post => {
    // For TikTok videos, check if post.info exists and use daily_stats
    if (post.socialNetwork === 'tiktok' && post.info) {
      try {
        const videoInfo =
          typeof post.info === 'string' ? JSON.parse(post.info) : post.info;

        // Use daily_stats from video.info if available
        if (videoInfo.daily_stats && Array.isArray(videoInfo.daily_stats)) {
          videoInfo.daily_stats.forEach(dailyStat => {
            const {
              date,
              likes,
              comments,
              shares,
              views,
              engagement_rate,
            } = dailyStat;
            if (!date) return;

            if (!dailyBreakdown[date]) {
              dailyBreakdown[date] = {
                likes: 0,
                comments: 0,
                shares: 0,
                dislikes: 0,
                views: 0,
                plays: 0,
                reach: 0,
                video_views_organic: 0,
                video_views_paid: 0,
                engagementRate: 0,
                videoCompletionRate: 0,
              };
            }

            // TikTok daily_stats already provides daily values, no need for cumulative calculation
            dailyBreakdown[date].likes += likes || 0;
            dailyBreakdown[date].comments += comments || 0;
            dailyBreakdown[date].shares += shares || 0;
            dailyBreakdown[date].views += views || 0;
            dailyBreakdown[date].engagementRate += engagement_rate || 0;

            // Add reach from TikTok daily stats if available
            if (dailyStat.reach !== undefined) {
              dailyBreakdown[date].reach += dailyStat.reach || 0;
            }

            // Add organic and paid views from TikTok daily stats if available
            if (dailyStat.video_views_organic !== undefined) {
              dailyBreakdown[date].video_views_organic +=
                dailyStat.video_views_organic || 0;
            }
            if (dailyStat.video_views_paid !== undefined) {
              dailyBreakdown[date].video_views_paid +=
                dailyStat.video_views_paid || 0;
            }

            // Add video completion rate from TikTok daily stats if available
            if (dailyStat.video_completion_rate !== undefined) {
              dailyBreakdown[date].videoCompletionRate +=
                dailyStat.video_completion_rate || 0;
            }
          });
        }
      } catch (error) {
        console.warn(
          'Error parsing daily stats for TikTok post in processDailyBreakdown:',
          error
        );
      }
    } else {
      // Fallback to original metrics logic for non-TikTok posts (Instagram, YouTube)
      const { metrics } = post;

      // Normalize: API may return a single metric object instead of an array
      let metricsArray = [];
      if (Array.isArray(metrics)) {
        metricsArray = metrics;
      } else if (
        metrics &&
        typeof metrics === 'object' &&
        metrics.date != null
      ) {
        metricsArray = [metrics];
      }

      if (metricsArray.length === 0) {
        return;
      }

      // Sort metrics by date to ensure proper chronological order
      const sortedMetrics = [...metricsArray].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      // Process metrics to convert cumulative values to daily values
      sortedMetrics.forEach((metric, index) => {
        const { date: rawDate, ...metricValues } = metric;
        if (rawDate == null) return;
        // Normalize to YYYY-MM-DD so it matches pre-filled keys when in range
        let date = null;
        if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
          date = rawDate.slice(0, 10);
        } else {
          const d = new Date(rawDate);
          date = Number.isNaN(d.getTime())
            ? null
            : d.toISOString().split('T')[0];
        }
        if (!date) return;

        const values = extractMetricValues(metricValues);

        if (!dailyBreakdown[date]) {
          dailyBreakdown[date] = {
            likes: 0,
            comments: 0,
            shares: 0,
            dislikes: 0,
            views: 0,
            plays: 0,
            reach: 0,
            video_views_organic: 0,
            video_views_paid: 0,
            engagementRate: 0,
            videoCompletionRate: 0,
          };
        }

        if (index === 0) {
          // First day: use the values as-is (they represent the first day's totals)
          dailyBreakdown[date].likes += values.likes;
          dailyBreakdown[date].comments += values.comments;
          dailyBreakdown[date].shares += values.shares;
          dailyBreakdown[date].dislikes += values.dislikes;
          dailyBreakdown[date].views += values.views;
          dailyBreakdown[date].plays += values.plays;
          dailyBreakdown[date].reach += values.reach;
          dailyBreakdown[date].video_views_organic +=
            values.video_views_organic;
          dailyBreakdown[date].video_views_paid += values.video_views_paid;
          dailyBreakdown[date].engagementRate += calculateEngagementRate(
            values.likes,
            values.comments,
            values.shares,
            values.views
          );
        } else {
          // Subsequent days: calculate the difference from the previous day
          const previousMetric = sortedMetrics[index - 1];
          const previousValues = extractMetricValues(previousMetric);

          dailyBreakdown[date].likes += values.likes - previousValues.likes;
          dailyBreakdown[date].comments +=
            values.comments - previousValues.comments;
          dailyBreakdown[date].shares += values.shares - previousValues.shares;
          dailyBreakdown[date].dislikes +=
            values.dislikes - previousValues.dislikes;
          dailyBreakdown[date].views += values.views - previousValues.views;
          dailyBreakdown[date].plays += values.plays - previousValues.plays;
          dailyBreakdown[date].reach += values.reach - previousValues.reach;
          dailyBreakdown[date].video_views_organic +=
            values.video_views_organic - previousValues.video_views_organic;
          dailyBreakdown[date].video_views_paid +=
            values.video_views_paid - previousValues.video_views_paid;

          dailyBreakdown[date].engagementRate = calculateEngagementRate(
            dailyBreakdown[date].likes,
            dailyBreakdown[date].comments,
            dailyBreakdown[date].shares,
            dailyBreakdown[date].views
          );
        }
      });
    }
  });

  return dailyBreakdown;
};

// Helper function to parse analysis data
const parseAnalysisData = analysis => {
  try {
    // analysis data is encoded twice by ML
    return JSON.parse(JSON.parse(analysis));
  } catch (error) {
    return null;
  }
};

// Helper function to get sentiment analysis from videos
const getSentimentAnalysis = posts => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return [];
  }

  const sentimentCounts = {};
  let totalVideosWithSentiment = 0;

  posts.forEach(post => {
    const analysisData = parseAnalysisData(post.analysis);
    const videoSentiment = analysisData?.analysis?.VideoSentiment;

    if (videoSentiment) {
      sentimentCounts[videoSentiment] =
        (sentimentCounts[videoSentiment] || 0) + 1;
      totalVideosWithSentiment += 1;
    }
  });

  if (totalVideosWithSentiment === 0) {
    return [];
  }

  // Convert counts to percentages and return as array
  return Object.entries(sentimentCounts).map(([sentiment, count]) => ({
    sentiment,
    count,
    percentage: ((count / totalVideosWithSentiment) * 100).toFixed(2),
  }));
};

// Helper function to extract media URL
const extractMediaURL = mediaURLs => {
  try {
    const urls = JSON.parse(mediaURLs);
    return _.isArray(urls) ? urls[0].url : urls.url;
  } catch (error) {
    return null;
  }
};

// Helper function to create video object for aggregation
const formatVideo = video => {
  const latestMetric = getLatestMetric(video.metrics);
  const analysisData = parseAnalysisData(video.analysis);

  // For TikTok videos, check if video.info exists and use it for metrics
  let comments = _.get(latestMetric, 'comments', 0);
  let likes = _.get(latestMetric, 'likes', 0);
  const dislikes = _.get(latestMetric, 'dislikes', 0);
  let shares = _.get(latestMetric, 'shares', 0);
  let views = _.get(latestMetric, 'views', 0);
  let engagementRate = _.get(latestMetric, 'engagement_rate', 0);
  let reach = _.get(latestMetric, 'reach', 0);
  let video_views_organic = _.get(latestMetric, 'video_views_organic', 0);
  let video_views_paid = _.get(latestMetric, 'video_views_paid', 0);
  let video_completion_rate = _.get(latestMetric, 'video_completion_rate', 0);

  // Check if this is a TikTok video with info data
  if (video.socialNetwork === 'tiktok' && video.info) {
    try {
      const videoInfo =
        typeof video.info === 'string' ? JSON.parse(video.info) : video.info;

      // Use metrics from video.info if available
      if (videoInfo.comments !== undefined) comments = videoInfo.comments;
      if (videoInfo.likes !== undefined) likes = videoInfo.likes;
      if (videoInfo.shares !== undefined) shares = videoInfo.shares;
      if (videoInfo.video_views !== undefined) views = videoInfo.video_views;
      if (videoInfo.engagement_rate !== undefined)
        engagementRate = videoInfo.engagement_rate;
      if (videoInfo.reach !== undefined) reach = videoInfo.reach;
      if (videoInfo.video_views_organic !== undefined)
        video_views_organic = videoInfo.video_views_organic;
      if (videoInfo.video_views_paid !== undefined)
        video_views_paid = videoInfo.video_views_paid;
      if (videoInfo.video_completion_rate !== undefined)
        video_completion_rate = videoInfo.video_completion_rate;
    } catch (error) {
      console.warn(
        'Error parsing video info for TikTok video in formatVideo:',
        error
      );
    }
  }

  return {
    ...video,
    key: video.url,
    comments,
    likes,
    dislikes,
    shares,
    views,
    engagementRate,
    reach,
    video_views_organic,
    video_views_paid,
    video_completion_rate,
    analysisData: _.isObject(analysisData) ? analysisData : null,
    RADScore: analysisData?.analysis?.radScore || null,
    createTime: video.publishDate,
    mediaURL: extractMediaURL(video.mediaURLs),
    postInterests: analysisData?.analysis?.videoInterests || [],
  };
};

const getAvgVideoEmotions = posts => {
  const results = {};
  let count = 0;

  posts.forEach(post => {
    const analysisData = parseAnalysisData(post.analysis);

    if (
      analysisData &&
      analysisData.analysis &&
      analysisData.analysis.videoEmotions
    ) {
      count += 1;
      const { videoEmotions } = analysisData.analysis;

      Object.keys(videoEmotions.emotions).forEach(emotion => {
        results[emotion] =
          (results[emotion] || 0) + videoEmotions.emotions[emotion];
      });
    }
  });

  Object.keys(results).forEach(emotion => {
    results[emotion] /= count;
  });

  return results;
};

const getDominantEmotion = posts => {
  const results = {};
  let count = 0;

  posts.forEach(post => {
    const analysisData = parseAnalysisData(post.analysis);

    if (
      analysisData &&
      analysisData.analysis &&
      analysisData.analysis.videoEmotions
    ) {
      count += 1;
      const { videoEmotions } = analysisData.analysis;

      Object.keys(videoEmotions.emotions).forEach(emotion => {
        results[emotion] =
          (results[emotion] || 0) + videoEmotions.emotions[emotion];
      });
    }
  });

  const dominantSentiment = Object.keys(results).reduce((max, emotion) => {
    return results[emotion] > results[max] ? emotion : max;
  }, 'Neutral');

  return dominantSentiment;
};

const getPlatformMetrics = posts => {
  const data = {
    totalViews: 0,
    totalReach: 0,
    totalLikes: 0,
    totalComments: 0,
    totalShares: 0,
    totalDislikes: 0,
    totalVideoViewsOrganic: 0,
    totalVideoViewsPaid: 0,
    avgEngagementRate: 0,
    dailyBreakdown: {},
  };

  let reportLastUpdatedDate = 'N/A';

  posts.forEach(post => {
    let views = 0;
    let reach = 0;
    let likes = 0;
    let comments = 0;
    let shares = 0;
    let dislikes = 0;
    let video_views_organic = 0;
    let video_views_paid = 0;
    let metricDate = null;

    // For TikTok videos, check if post.info exists and use it for metrics
    if (post.socialNetwork === 'tiktok' && post.info) {
      try {
        const videoInfo =
          typeof post.info === 'string' ? JSON.parse(post.info) : post.info;

        // Use metrics from video.info if available
        if (videoInfo.video_views !== undefined) views = videoInfo.video_views;
        if (videoInfo.reach !== undefined) reach = videoInfo.reach;
        if (videoInfo.likes !== undefined) likes = videoInfo.likes;
        if (videoInfo.comments !== undefined) comments = videoInfo.comments;
        if (videoInfo.shares !== undefined) shares = videoInfo.shares;
        if (videoInfo.dislikes !== undefined) dislikes = videoInfo.dislikes;
        if (videoInfo.video_views_organic !== undefined)
          video_views_organic = videoInfo.video_views_organic;
        if (videoInfo.video_views_paid !== undefined)
          video_views_paid = videoInfo.video_views_paid;

        // Use create_time from video_info if available
        if (videoInfo.video_info && videoInfo.video_info.create_time) {
          metricDate = new Date(videoInfo.video_info.create_time);
        }
      } catch (error) {
        console.warn(
          'Error parsing video info for TikTok post in getPlatformMetrics:',
          error
        );
      }
    } else {
      // Fallback to original metrics logic for non-TikTok posts
      const { metrics } = post;
      const latestMetric = getLatestMetric(metrics);

      if (latestMetric) {
        const values = extractMetricValues(latestMetric);
        views = values.views;
        reach = values.reach;
        likes = values.likes;
        comments = values.comments;
        shares = values.shares;
        dislikes = values.dislikes;
        video_views_organic = values.video_views_organic;
        video_views_paid = values.video_views_paid;

        if (latestMetric.date) {
          metricDate = new Date(latestMetric.date);
        }
      }
    }

    // Calculate engagement rate for this post to check if it should be excluded
    const postEngagementRate = calculateEngagementRate(
      likes,
      comments,
      shares,
      views
    );

    // Don't count videos whose engagement_rate is exactly 0
    if (postEngagementRate !== 0) {
      // Aggregate totals from the LATEST metric only (each post counted once)
      data.totalViews += views;
      data.totalReach += reach;
      data.totalLikes += likes;
      data.totalComments += comments;
      data.totalShares += shares;
      data.totalDislikes += dislikes;

      // Only aggregate organic and paid views for TikTok posts
      if (post.socialNetwork === 'tiktok') {
        data.totalVideoViewsOrganic += video_views_organic;
        data.totalVideoViewsPaid += video_views_paid;
      }
    }

    // Track the latest metrics date for reportLastUpdatedDate
    if (metricDate) {
      if (
        reportLastUpdatedDate === 'N/A' ||
        metricDate > new Date(reportLastUpdatedDate)
      ) {
        const [datePart] = metricDate.toISOString().split('T');
        reportLastUpdatedDate = datePart;
      }
    }
  });

  data.avgEngagementRate = calculateEngagementRate(
    data.totalLikes,
    data.totalComments,
    data.totalShares,
    data.totalViews
  );

  data.dailyBreakdown = processDailyBreakdown(posts);
  data.reportLastUpdatedDate = reportLastUpdatedDate;
  return data;
};

const processGeneralPosts = posts => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return {
      totalViews: 0,
      avgEngagementRate: 0,
      totalReach: 0,
      totalComments: 0,
      totalLikes: 0,
      totalDislikes: 0,
      totalShares: 0,
      totalVideoViewsOrganic: 0,
      totalVideoViewsPaid: 0,
      videos: [],
      dailyBreakdown: {},
      // Add sentiment tracking
      avgSentimentByEmotions: {
        Angry: 0,
        Disgust: 0,
        Fear: 0,
        Happy: 0,
        Sad: 0,
        Surprise: 0,
        Neutral: 0,
      },
      reportLastUpdatedDate: 'N/A',
      avgVideoCompletionRate: 0,
    };
  }

  // Process each post
  const aggregatedMetricsData = getPlatformMetrics(posts);
  // Calculate average engagement rate

  // Calculate content quality metrics for TikTok
  let totalViewTime = 0;
  let twoSecondVideoViewsSum = 0;
  let sixSecondVideoViewsSum = 0;
  let viewTimeCount = 0;
  let avgViewTimeSum = 0;
  let avgViewTimeCount = 0;
  let twoSecondCount = 0;
  let sixSecondCount = 0;
  let videoCompletionRateSum = 0;
  let videoCompletionRateCount = 0;
  const videoViewsBySource = {};

  // Demographics aggregation
  const deviceDistribution = {};
  const genderDistribution = {};
  const ageDistribution = {};
  const countryDistribution = {};
  const interestDistribution = {};

  posts.forEach(post => {
    if (post.socialNetwork === 'tiktok' && post.info) {
      try {
        const videoInfo =
          typeof post.info === 'string' ? JSON.parse(post.info) : post.info;

        // Calculate total view time
        if (videoInfo.total_play_time) {
          totalViewTime += videoInfo.total_play_time;
          viewTimeCount += 1;
        }

        // Calculate average view time directly from average_view_time
        if (videoInfo.average_view_time) {
          avgViewTimeSum += videoInfo.average_view_time;
          avgViewTimeCount += 1;
        }

        // Calculate 2-second video views as percentage (sum for average)
        if (videoInfo.two_seconds_views) {
          twoSecondVideoViewsSum += videoInfo.two_seconds_views;
          twoSecondCount += 1;
        }

        // Calculate 6-second video views as percentage (sum for average)
        if (videoInfo.six_seconds_views) {
          sixSecondVideoViewsSum += videoInfo.six_seconds_views;
          sixSecondCount += 1;
        }

        // Calculate video completion rate (sum for average)
        if (videoInfo.video_completion_rate) {
          videoCompletionRateSum += videoInfo.video_completion_rate;
          videoCompletionRateCount += 1;
        }

        // Aggregate video views by source
        if (
          videoInfo.video_views_by_source &&
          Array.isArray(videoInfo.video_views_by_source)
        ) {
          videoInfo.video_views_by_source.forEach(source => {
            if (source.source && source.videoViews) {
              videoViewsBySource[source.source] =
                (videoViewsBySource[source.source] || 0) + source.videoViews;
            }
          });
        }

        // Aggregate device distribution
        if (
          videoInfo.audience_device_distribution &&
          Array.isArray(videoInfo.audience_device_distribution)
        ) {
          videoInfo.audience_device_distribution.forEach(device => {
            if (device.category && device.percentage) {
              const percentage = parseFloat(device.percentage);
              deviceDistribution[device.category] =
                (deviceDistribution[device.category] || 0) + percentage;
            }
          });
        }

        // Aggregate gender distribution
        if (
          videoInfo.audience_genders_distribution &&
          Array.isArray(videoInfo.audience_genders_distribution)
        ) {
          videoInfo.audience_genders_distribution.forEach(gender => {
            if (gender.category && gender.percentage) {
              const percentage = parseFloat(gender.percentage);
              genderDistribution[gender.category] =
                (genderDistribution[gender.category] || 0) + percentage;
            }
          });
        }

        // Aggregate age distribution
        if (
          videoInfo.audience_age_distribution &&
          Array.isArray(videoInfo.audience_age_distribution)
        ) {
          videoInfo.audience_age_distribution.forEach(age => {
            if (age.age && age.percentage) {
              const percentage = parseFloat(age.percentage);
              ageDistribution[age.age] =
                (ageDistribution[age.age] || 0) + percentage;
            }
          });
        }

        // Aggregate country distribution
        if (
          videoInfo.audience_countries_distribution &&
          Array.isArray(videoInfo.audience_countries_distribution)
        ) {
          videoInfo.audience_countries_distribution.forEach(country => {
            if (country.category && country.percentage) {
              const percentage = parseFloat(country.percentage);
              countryDistribution[country.category] =
                (countryDistribution[country.category] || 0) + percentage;
            }
          });
        }

        // Aggregate interest distribution
        if (
          videoInfo.audience_interest_distribution &&
          Array.isArray(videoInfo.audience_interest_distribution)
        ) {
          videoInfo.audience_interest_distribution.forEach(interest => {
            if (interest.interest_name && interest.percentage) {
              const percentage = parseFloat(interest.percentage);
              interestDistribution[interest.interest_name] =
                (interestDistribution[interest.interest_name] || 0) +
                percentage;
            }
          });
        }
      } catch (error) {
        console.warn('Error parsing video info for TikTok post:', error);
      }
    }
  });

  // Calculate average view time
  const avgViewTime =
    avgViewTimeCount > 0 ? avgViewTimeSum / avgViewTimeCount : 0;

  // Calculate average 2-second video views
  const totalTwoSecondVideoViews =
    twoSecondCount > 0 ? twoSecondVideoViewsSum / twoSecondCount : 0;

  // Calculate average 6-second video views
  const totalSixSecondVideoViews =
    sixSecondCount > 0 ? sixSecondVideoViewsSum / sixSecondCount : 0;

  // Calculate average video completion rate
  const avgVideoCompletionRate =
    videoCompletionRateCount > 0
      ? videoCompletionRateSum / videoCompletionRateCount
      : 0;

  // Convert aggregated video views by source to array format
  const videoViewsBySourceArray = Object.entries(videoViewsBySource).map(
    ([source, videoViews]) => ({
      source,
      videoViews,
    })
  );

  // Calculate average demographics distributions
  const postCount = posts.filter(post => post.socialNetwork === 'tiktok')
    .length;
  const avgPostCount = postCount > 0 ? postCount : 1;

  // Calculate averages and normalize to 100%
  const calculateNormalizedAverages = distribution => {
    const averages = Object.entries(distribution).map(
      ([key, totalPercentage]) => ({
        key,
        percentage: totalPercentage / avgPostCount,
      })
    );

    // Calculate total percentage
    const totalPercentage = averages.reduce(
      (sum, item) => sum + item.percentage,
      0
    );

    // Normalize to 100% if total is not 0
    if (totalPercentage > 0) {
      return averages.map(item => ({
        ...item,
        percentage: ((item.percentage / totalPercentage) * 100).toFixed(2),
      }));
    }

    return averages.map(item => ({
      ...item,
      percentage: item.percentage.toFixed(2),
    }));
  };

  // Convert device distribution to array format with normalized averages
  const platformShares = calculateNormalizedAverages(deviceDistribution).map(
    item => ({
      category: item.key,
      percentage: item.percentage,
    })
  );

  // Convert gender distribution to array format with normalized averages
  const totalLifetimeGenderDistribution = calculateNormalizedAverages(
    genderDistribution
  ).map(item => ({
    category: item.key,
    percentage: item.percentage,
  }));

  // Convert age distribution to array format with normalized averages
  const totalLifetimeTopAgeDistribution = calculateNormalizedAverages(
    ageDistribution
  ).map(item => ({
    age: item.key,
    percentage: item.percentage,
  }));

  // Convert country distribution to array format with normalized averages
  const totalLifetimeCountryDistribution = calculateNormalizedAverages(
    countryDistribution
  ).map(item => ({
    category: item.key,
    percentage: item.percentage,
  }));

  // Convert interest distribution to array format with normalized averages
  const totalLifetimeInterestDistribution = calculateNormalizedAverages(
    interestDistribution
  ).map(item => ({
    category: item.key,
    percentage: item.percentage,
  }));

  return {
    ...aggregatedMetricsData,
    dominantSentiment: getDominantEmotion(posts),
    videos: posts.map(post => formatVideo(post)),
    avgSentimentByEmotions: getAvgVideoEmotions(posts),
    // Add content quality metrics
    totalViewTime,
    avgViewTime,
    totalTwoSecondVideoViews,
    totalSixSecondVideoViews,
    avgVideoCompletionRate,
    video_views_by_source: videoViewsBySourceArray,
    // Add demographics metrics
    platformShares,
    totalLifetimeGenderDistribution,
    totalLifetimeTopAgeDistribution,
    totalLifetimeCountryDistribution,
    totalLifetimeInterestDistribution,
    sentimentAnalysis: getSentimentAnalysis(posts),
  };
};

// Helper function to aggregate data across every supported platform.
// Accepts an array of per-network processed data objects so any supported
// network (e.g. LinkedIn) is included without hardcoding the platform list.
const aggregateAllPlatforms = (platformDatas = []) => {
  const aggregated = {
    totalViews: 0,
    avgEngagementRate: 0,
    totalReach: 0,
    totalComments: 0,
    totalLikes: 0,
    totalDislikes: 0,
    totalShares: 0,
    totalVideoViewsOrganic: 0,
    totalVideoViewsPaid: 0,
    videos: [],
    dailyBreakdown: {},
    avgSentimentByEmotions: {
      Angry: 0,
      Disgust: 0,
      Fear: 0,
      Happy: 0,
      Sad: 0,
      Surprise: 0,
      Neutral: 0,
    },
    dominantSentiment: '',
    reportLastUpdatedDate: 'N/A',
    avgVideoCompletionRate: 0,
    sentimentAnalysis: [],
  };

  // Aggregate sentiment analysis counts from all platforms
  const sentimentCounts = {};
  let totalSentimentCount = 0;

  // Aggregate totals from all platforms
  platformDatas.forEach(platformData => {
    if (platformData) {
      aggregated.totalViews += platformData.totalViews || 0;
      aggregated.totalReach += platformData.totalReach || 0;
      aggregated.totalLikes += platformData.totalLikes || 0;
      aggregated.totalComments += platformData.totalComments || 0;
      aggregated.totalShares += platformData.totalShares || 0;
      aggregated.totalDislikes += platformData.totalDislikes || 0;
      aggregated.totalVideoViewsOrganic +=
        platformData.totalVideoViewsOrganic || 0;
      aggregated.totalVideoViewsPaid += platformData.totalVideoViewsPaid || 0;

      // Combine videos from all platforms
      if (platformData.videos && Array.isArray(platformData.videos)) {
        aggregated.videos.push(...platformData.videos);
      }

      // Track the latest metrics date across all platforms
      if (
        platformData.reportLastUpdatedDate &&
        platformData.reportLastUpdatedDate !== 'N/A'
      ) {
        const platformDate = new Date(platformData.reportLastUpdatedDate);
        const currentLatest = new Date(aggregated.reportLastUpdatedDate);

        if (
          aggregated.reportLastUpdatedDate === 'N/A' ||
          platformDate > currentLatest
        ) {
          aggregated.reportLastUpdatedDate = platformData.reportLastUpdatedDate;
        }
      }

      if (platformData.avgVideoCompletionRate) {
        aggregated.avgVideoCompletionRate += platformData.avgVideoCompletionRate;
      }

      // Aggregate sentiment analysis
      if (
        platformData.sentimentAnalysis &&
        Array.isArray(platformData.sentimentAnalysis)
      ) {
        platformData.sentimentAnalysis.forEach(item => {
          sentimentCounts[item.sentiment] =
            (sentimentCounts[item.sentiment] || 0) + item.count;
          totalSentimentCount += item.count;
        });
      }

      // Aggregate dominant emotion counts
      if (platformData.dominantSentimentCounts) {
        Object.entries(platformData.dominantSentimentCounts).forEach(
          ([emotion, count]) => {
            aggregated.dominantSentimentCounts[emotion] =
              (aggregated.dominantSentimentCounts[emotion] || 0) + count;
          }
        );
      }
    }
  });

  // Calculate average sentiment by emotions across all platforms
  // We need to collect all emotion values and calculate the true average
  const allEmotionValues = {
    Angry: [],
    Disgust: [],
    Fear: [],
    Happy: [],
    Sad: [],
    Surprise: [],
    Neutral: [],
  };

  // Collect emotion values from all platforms
  platformDatas.forEach(platformData => {
    if (platformData && platformData.avgSentimentByEmotions) {
      Object.keys(platformData.avgSentimentByEmotions).forEach(emotion => {
        if (
          allEmotionValues[emotion] &&
          platformData.avgSentimentByEmotions[emotion] > 0
        ) {
          allEmotionValues[emotion].push(
            platformData.avgSentimentByEmotions[emotion]
          );
        }
      });
    }
  });

  // Calculate the average for each emotion
  Object.keys(allEmotionValues).forEach(emotion => {
    if (allEmotionValues[emotion].length > 0) {
      aggregated.avgSentimentByEmotions[emotion] =
        allEmotionValues[emotion].reduce((sum, val) => sum + val, 0) /
        allEmotionValues[emotion].length;
    } else {
      aggregated.avgSentimentByEmotions[emotion] = 0;
    }
  });

  // Calculate overall average engagement rate
  const totalEngagementRate = platformDatas
    .filter(data => data && data.avgEngagementRate !== undefined)
    .reduce((sum, data) => sum + data.avgEngagementRate, 0);

  const platformCount = platformDatas.filter(
    data => data && data.avgEngagementRate !== undefined
  ).length;

  aggregated.avgEngagementRate =
    platformCount > 0 ? totalEngagementRate / platformCount : 0;

  // Merge daily breakdown data from all platforms
  const allDailyBreakdowns = platformDatas
    .filter(data => data && data.dailyBreakdown)
    .map(data => data.dailyBreakdown);

  if (allDailyBreakdowns.length > 0) {
    // Get the date range for the last 3 months
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    // Initialize all dates in the last 3 months with 0s
    const currentDate = new Date(threeMonthsAgo);
    while (currentDate <= today) {
      const dateString = currentDate.toISOString().split('T')[0];
      aggregated.dailyBreakdown[dateString] = {
        likes: 0,
        comments: 0,
        shares: 0,
        dislikes: 0,
        views: 0,
        plays: 0,
        reach: 0,
        video_views_organic: 0,
        video_views_paid: 0,
        engagementRate: 0,
        videoCompletionRate: 0,
      };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Sum up daily metrics from all platforms
    allDailyBreakdowns.forEach(platformBreakdown => {
      Object.entries(platformBreakdown).forEach(([date, metrics]) => {
        if (aggregated.dailyBreakdown[date]) {
          aggregated.dailyBreakdown[date].likes += metrics.likes || 0;
          aggregated.dailyBreakdown[date].comments += metrics.comments || 0;
          aggregated.dailyBreakdown[date].shares += metrics.shares || 0;
          aggregated.dailyBreakdown[date].dislikes += metrics.dislikes || 0;
          aggregated.dailyBreakdown[date].views += metrics.views || 0;
          aggregated.dailyBreakdown[date].plays += metrics.plays || 0;
          aggregated.dailyBreakdown[date].reach += metrics.reach || 0;
          aggregated.dailyBreakdown[date].video_views_organic +=
            metrics.video_views_organic || 0;
          aggregated.dailyBreakdown[date].video_views_paid +=
            metrics.video_views_paid || 0;
          aggregated.dailyBreakdown[date].engagementRate +=
            metrics.engagementRate || 0;
          aggregated.dailyBreakdown[date].videoCompletionRate +=
            metrics.videoCompletionRate || 0;
        }
      });
    });
  }

  aggregated.dominantSentiment = getDominantEmotion(platformDatas.flat());

  // Convert aggregated sentiment counts to array with percentages
  if (totalSentimentCount > 0) {
    aggregated.sentimentAnalysis = Object.entries(sentimentCounts).map(
      ([sentiment, count]) => ({
        sentiment,
        count,
        percentage: ((count / totalSentimentCount) * 100).toFixed(2),
      })
    );
  }

  return aggregated;
};

export const processReportingData = posts => {
  // Build a per-network bucket for every supported platform (incl. LinkedIn)
  // so no network is silently dropped. Keyed by registry network key.
  const perNetwork = SUPPORTED_NETWORKS.reduce((acc, network) => {
    acc[network] = processGeneralPosts(
      posts.filter(post => post.socialNetwork === network)
    );
    return acc;
  }, {});

  return {
    ...perNetwork,
    all: aggregateAllPlatforms(Object.values(perNetwork)),
  };
};